// CompactViewToolbar.jsx

import React from 'react';

// CSS
import './CompactViewToolbar.css';

// Components
import Divider from 'components/divider/Divider';
import CatchAllViewTypeButtons from 'views/catchall/CatchAllViewTypeButtons';

/**
 * CompactViewToolbar
 *
 * Renders the compact view-switching row shared across node and collection surfaces.
 *
 * @param {object} theme - Theme tokens used for icon and divider colors.
 * @param {string|null} selectedType - Selected catch-all view type.
 * @param {function} onSelectType - Called when the catch-all view type changes.
 * @param {ReactNode} draftControls - Filter / save cluster rendered after catch-all types.
 * @param {ReactNode} viewsControl - Primary views selector control.
 * @param {ReactNode} viewChips - Optional saved-view chips rendered with the views cluster.
 * @param {ReactNode} utilityControls - Trailing display/search controls.
 * @param {string} dividerMargin - Divider margin string.
 * @param {string} className - Optional extra class names.
 * @param {string} viewTitlePrefix - Optional prefix for catch-all view-type button titles.
 *
 * @returns {ReactNode} The rendered compact toolbar row.
 */
const CompactViewToolbar = React.memo(({
  theme,
  selectedType = null,
  onSelectType,
  draftControls = null,
  viewsControl = null,
  viewChips = null,
  utilityControls = null,
  dividerMargin = '6px',
  className = '',
  types,
  forceCompactTypeSelector = false,
  viewTitlePrefix = '',
}) => {

  // ----- RENDER ----- //

  return (
    <div className={`compact-view-toolbar-container${className ? ` ${className}` : ''}`}>

      <CatchAllViewTypeButtons
        theme={theme}
        selectedType={selectedType}
        types={types}
        forceCompact={forceCompactTypeSelector}
        viewTitlePrefix={viewTitlePrefix}
        onSelectType={onSelectType}
      />

      {draftControls ? (
        <>
          <Divider
            direction="vertical"
            height={20}
            margin={dividerMargin}
            color={theme?.outlineMuted}
          />

          {/* DRAFT GROUP */}
          <div className="compact-view-toolbar-draft-group">
            {draftControls}
          </div>
        </>
      ) : null}

      <Divider
        direction="vertical"
        height={20}
        margin={dividerMargin}
        color={theme?.outlineMuted}
      />

      {/* VIEWS GROUP */}
      <div className="compact-view-toolbar-views-group">
        {viewsControl}
        {viewChips}
      </div>

      {utilityControls ? (
        <>
          <Divider
            direction="vertical"
            height={20}
            margin={dividerMargin}
            color={theme?.outlineMuted}
          />

          {/* UTILITY GROUP */}
          <div className="compact-view-toolbar-utility-group">
            {utilityControls}
          </div>
        </>
      ) : null}
    </div>
  );
});

// Dev Tools
CompactViewToolbar.displayName = 'CompactViewToolbar';

export default CompactViewToolbar;
